import { Eye, FileText, Lock, Mail, MapPin, Phone, Shield, Users } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';

const PrivacyPolicy = () => {
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 pb-8">
            <PageHeader title="Privacy Policy" subNav="Privacy Policy" />
            <div className="container">

                <div className="my-5 text-sm text-gray-500 font-semibold">
                    Last Updated: May 30, 2025
                </div>

                {/* Content */}
                <div className="">
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                        <div className="sm:p-8 p-5 md:space-y-12 space-y-8">

                            {/* Introduction */}
                            <section>
                                <div className="flex items-center gap-3 sm:mb-6 mb-4">
                                    <Eye className="w-6 h-6 text-blue-600" />
                                    <h2 className="md:text-2xl sm:text-xl text-lg font-semibold text-gray-900">Introduction</h2>
                                </div>
                                <div className="prose prose-gray max-w-none">
                                    <p className="max-sm:text-sm text-gray-700 leading-relaxed">
                                        Argon Medical Systems LLP ("we," "our," or "us") is committed to protecting your privacy and personal information.
                                        This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website
                                        www.argonmedicalsystems.com (the "Website") or interact with our medical device products and services.
                                    </p>
                                    <p className="max-sm:text-sm text-gray-700 leading-relaxed mt-4">
                                        As a medical device company serving healthcare professionals and institutions, we understand the critical importance
                                        of maintaining the confidentiality and security of personal and health-related information in accordance with applicable
                                        healthcare regulations including HIPAA, GDPR, and other relevant privacy laws.
                                    </p>
                                </div>
                            </section>

                            {/* Information We Collect */}
                            <section>
                                <div className="flex items-center gap-3 sm:mb-6 mb-4">
                                    <FileText className="w-6 h-6 text-blue-600" />
                                    <h2 className="md:text-2xl sm:text-xl text-lg font-semibold text-gray-900">Information We Collect</h2>
                                </div>

                                <div className="space-y-6">
                                    <div className="bg-gray-50 rounded-lg sm:p-6 p-4">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Personal Information</h3>
                                        <ul className="space-y-2 text-gray-700">
                                            <li className='max-sm:text-sm'>• Name, email address, phone number, and mailing address</li>
                                            <li className='max-sm:text-sm'>• Professional credentials and medical license information</li>
                                            <li className='max-sm:text-sm'>• Hospital or clinic affiliation details</li>
                                            <li className='max-sm:text-sm'>• Job title and department information</li>
                                            <li className='max-sm:text-sm'>• Account login credentials for our systems</li>
                                        </ul>
                                    </div>

                                    <div className="bg-gray-50 rounded-lg sm:p-6 p-4">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Technical Information</h3>
                                        <ul className="space-y-2 text-gray-700">
                                            <li className='max-sm:text-sm'>• IP address, browser type, and operating system</li>
                                            <li className='max-sm:text-sm'>• Device identifiers and mobile network information</li>
                                            <li className='max-sm:text-sm'>• Website usage data and analytics</li>
                                            <li className='max-sm:text-sm'>• Cookies and similar tracking technologies</li>
                                            <li className='max-sm:text-sm'>• Log files and server data</li>
                                        </ul>
                                    </div>

                                    <div className="bg-gray-50 rounded-lg sm:p-6 p-4">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Professional Information</h3>
                                        <ul className="space-y-2 text-gray-700">
                                            <li className='max-sm:text-sm'>• Product usage and training records</li>
                                            <li className='max-sm:text-sm'>• Service requests and support communications</li>
                                            <li className='max-sm:text-sm'>• Medical device performance data (anonymized)</li>
                                            <li className='max-sm:text-sm'>• Professional development and certification records</li>
                                            <li className='max-sm:text-sm'>• Conference and event attendance information</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>

                            {/* How We Use Information */}
                            <section>
                                <div className="flex items-center gap-3 sm:mb-6 mb-4">
                                    <Users className="w-6 h-6 text-blue-600" />
                                    <h2 className="md:text-2xl sm:text-xl text-lg font-semibold text-gray-900">How We Use Your Information</h2>
                                </div>

                                <div className="grid md:grid-cols-2 gap-6">
                                    <div className="bg-blue-50 rounded-lg sm:p-6 p-4">
                                        <h3 className="text-lg font-semibold text-blue-900 mb-3">Primary Uses</h3>
                                        <ul className="space-y-2 text-blue-800">
                                            <li className='max-sm:text-sm'>• Provide and maintain our medical device products</li>
                                            <li className='max-sm:text-sm'>• Process orders and manage customer accounts</li>
                                            <li className='max-sm:text-sm'>• Provide technical support and customer service</li>
                                            <li className='max-sm:text-sm'>• Conduct product training and education</li>
                                            <li className='max-sm:text-sm'>• Ensure regulatory compliance and safety</li>
                                        </ul>
                                    </div>

                                    <div className="bg-green-50 rounded-lg sm:p-6 p-4">
                                        <h3 className="text-lg font-semibold text-green-900 mb-3">Secondary Uses</h3>
                                        <ul className="space-y-2 text-green-800">
                                            <li className='max-sm:text-sm'>• Improve product design and functionality</li>
                                            <li className='max-sm:text-sm'>• Conduct research and development</li>
                                            <li className='max-sm:text-sm'>• Send relevant product updates and notifications</li>
                                            <li className='max-sm:text-sm'>• Analyze website usage and performance</li>
                                            <li className='max-sm:text-sm'>• Comply with legal and regulatory requirements</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>

                            {/* Information Sharing */}
                            <section>
                                <div className="flex items-center gap-3 sm:mb-6 mb-4">
                                    <Lock className="w-6 h-6 text-blue-600" />
                                    <h2 className="md:text-2xl sm:text-xl text-lg font-semibold text-gray-900">Information Sharing and Disclosure</h2>
                                </div>

                                <div className="bg-amber-50 border border-amber-200 rounded-lg sm:p-6 p-4 sm:mb-6 mb-4">
                                    <h3 className="sm:text-lg font-semibold text-amber-900 mb-3">We Do Not Sell Personal Information</h3>
                                    <p className="max-sm:text-sm text-amber-800">
                                        We do not sell, trade, or rent your personal information to third parties for commercial purposes.
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900">We may share information in the following circumstances:</h3>
                                    <ul className="space-y-2 text-gray-700 ml-4">
                                        <li className='max-sm:text-sm'>• <strong>Service Providers:</strong> Trusted third-party vendors who assist with our operations</li>
                                        <li className='max-sm:text-sm'>• <strong>Business Partners:</strong> Authorized distributors and healthcare institutions</li>
                                        <li className='max-sm:text-sm'>• <strong>Legal Requirements:</strong> When required by law, regulation, or legal process</li>
                                        <li className='max-sm:text-sm'>• <strong>Safety and Security:</strong> To protect the rights, safety, or property of our users</li>
                                        <li className='max-sm:text-sm'>• <strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
                                        <li className='max-sm:text-sm'>• <strong>Regulatory Authorities:</strong> FDA, CE marking bodies, and other medical device regulators</li>
                                    </ul>
                                </div>
                            </section>

                            {/* Data Security */}
                            <section>
                                <div className="flex items-center gap-3 sm:mb-6 mb-4">
                                    <Shield className="w-6 h-6 text-blue-600" />
                                    <h2 className="md:text-2xl sm:text-xl text-lg font-semibold text-gray-900">Data Security</h2>
                                </div>

                                <div className="bg-gray-50 rounded-lg sm:p-6 p-4">
                                    <p className="max-sm:text-sm text-gray-700 leading-relaxed mb-4">
                                        We implement appropriate technical and organizational security measures to protect your personal information against
                                        unauthorized access, alteration, disclosure, or destruction. Our security measures include:
                                    </p>

                                    <div className="grid md:grid-cols-2 gap-4">
                                        <ul className="space-y-2 text-gray-700">
                                            <li className='max-sm:text-sm'>• Encryption of data in transit and at rest</li>
                                            <li className='max-sm:text-sm'>• Multi-factor authentication systems</li>
                                            <li className='max-sm:text-sm'>• Regular security audits and assessments</li>
                                            <li className='max-sm:text-sm'>• Employee security training and access controls</li>
                                        </ul>
                                        <ul className="space-y-2 text-gray-700">
                                            <li className='max-sm:text-sm'>• Secure data centers and cloud infrastructure</li>
                                            <li className='max-sm:text-sm'>• Incident response and breach notification procedures</li>
                                            <li className='max-sm:text-sm'>• HIPAA compliance for health information</li>
                                            <li className='max-sm:text-sm'>• ISO 27001 and SOC 2 compliance frameworks</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>

                            {/* Your Rights */}
                            <section>
                                <div className="flex items-center gap-3 sm:mb-6 mb-4">
                                    <Users className="w-6 h-6 text-blue-600" />
                                    <h2 className="md:text-2xl sm:text-xl text-lg font-semibold text-gray-900">Your Rights and Choices</h2>
                                </div>

                                <div className="grid md:grid-cols-2 gap-6">
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-900">Data Rights</h3>
                                        <ul className="space-y-2 text-gray-700">
                                            <li className='max-sm:text-sm'>• Access your personal information</li>
                                            <li className='max-sm:text-sm'>• Correct inaccurate information</li>
                                            <li className='max-sm:text-sm'>• Delete your personal information</li>
                                            <li className='max-sm:text-sm'>• Restrict processing of your data</li>
                                            <li className='max-sm:text-sm'>• Data portability rights</li>
                                            <li className='max-sm:text-sm'>• Object to certain processing activities</li>
                                        </ul>
                                    </div>

                                    <div className="space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-900">Communication Preferences</h3>
                                        <ul className="space-y-2 text-gray-700">
                                            <li className='max-sm:text-sm'>• Opt-out of marketing communications</li>
                                            <li className='max-sm:text-sm'>• Choose communication channels</li>
                                            <li className='max-sm:text-sm'>• Update contact preferences</li>
                                            <li className='max-sm:text-sm'>• Manage cookie settings</li>
                                            <li className='max-sm:text-sm'>• Control data sharing preferences</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>

                            {/* International Transfers */}
                            <section>
                                <div className="bg-blue-50 rounded-lg sm:p-6 p-4">
                                    <h3 className="text-lg font-semibold text-blue-900 mb-3">International Data Transfers</h3>
                                    <p className="max-sm:text-sm text-blue-800 leading-relaxed">
                                        As a global medical device company, we may transfer your personal information to countries outside of your
                                        residence. We ensure appropriate safeguards are in place, including standard contractual clauses, adequacy
                                        decisions, and compliance with applicable international data protection frameworks such as GDPR and similar regulations.
                                    </p>
                                </div>
                            </section>

                            {/* Retention */}
                            <section>
                                <div className="flex items-center gap-3 sm:mb-6 mb-4">
                                    <FileText className="w-6 h-6 text-blue-600" />
                                    <h2 className="md:text-2xl sm:text-xl text-lg font-semibold text-gray-900">Data Retention</h2>
                                </div>

                                <p className="max-sm:text-sm text-gray-700 leading-relaxed mb-4">
                                    We retain your personal information for as long as necessary to fulfill the purposes for which it was collected,
                                    including to satisfy legal, regulatory, accounting, or reporting requirements. Retention periods vary based on:
                                </p>

                                <ul className="space-y-2 text-gray-700 ml-4">
                                    <li className='max-sm:text-sm'>• The nature of the information and its purpose</li>
                                    <li className='max-sm:text-sm'>• Medical device regulatory requirements (typically 10+ years)</li>
                                    <li className='max-sm:text-sm'>• Legal obligations and statute of limitations</li>
                                    <li className='max-sm:text-sm'>• Legitimate business interests and customer relationships</li>
                                    <li className='max-sm:text-sm'>• Your consent and preferences</li>
                                </ul>
                            </section>

                            {/* Children's Privacy */}
                            <section>
                                <div className="bg-red-50 border border-red-200 rounded-lg sm:p-6 p-4">
                                    <h3 className="text-lg font-semibold text-red-900 mb-3">Children's Privacy</h3>
                                    <p className="max-sm:text-sm text-red-800 leading-relaxed">
                                        Our services are designed for healthcare professionals and institutions. We do not knowingly collect personal
                                        information from children under 13 years of age. If we become aware that we have collected such information,
                                        we will take steps to delete it promptly.
                                    </p>
                                </div>
                            </section>

                            {/* Changes to Policy */}
                            <section>
                                <div className="flex items-center gap-3 sm:mb-6 mb-4">
                                    <FileText className="w-6 h-6 text-blue-600" />
                                    <h2 className="md:text-2xl sm:text-xl text-lg font-semibold text-gray-900">Changes to This Privacy Policy</h2>
                                </div>

                                <p className="max-sm:text-sm text-gray-700 leading-relaxed">
                                    We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal
                                    requirements, or other factors. We will notify you of any material changes by posting the updated policy on
                                    our website and updating the "Last Updated" date. For significant changes, we may provide additional notice
                                    through email or other communications.
                                </p>
                            </section>

                            {/* Contact Information */}
                            <section>
                                <div className="flex items-center gap-3 sm:mb-6 mb-4">
                                    <Mail className="w-6 h-6 text-blue-600" />
                                    <h2 className="md:text-2xl sm:text-xl text-lg font-semibold text-gray-900">Contact Us</h2>
                                </div>

                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg sm:p-6 p-4">
                                    <p className="max-sm:text-sm text-gray-700 leading-relaxed sm:mb-6 mb-4">
                                        If you have questions about this Privacy Policy or our privacy practices, or if you would like to exercise
                                        your data protection rights, please contact us:
                                    </p>

                                    <div className="grid md:grid-cols-2 md:gap-6 gap-4">
                                        <div className="space-y-4">
                                            <div className="flex max-sm:flex-col sm:items-center gap-3">
                                                <Mail className="shrink-0 w-5 h-5 text-blue-600" />
                                                <div>
                                                    <p className="font-semibold text-gray-900">Email</p>
                                                    <p className="max-sm:text-sm text-gray-700">argonmedicalsystemsllp@gmail.com</p>
                                                </div>
                                            </div>

                                            <div className="flex max-sm:flex-col sm:items-center gap-3">
                                                <Phone className="shrink-0 w-5 h-5 text-blue-600" />
                                                <div>
                                                    <p className="font-semibold text-gray-900">Phone</p>
                                                    <p className="max-sm:text-sm text-gray-700">87581 50698, 79903 29076</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div className="flex max-sm:flex-col sm:items-start gap-3">
                                                <MapPin className="shrink-0 w-5 h-5 text-blue-600 mt-1" />
                                                <div>
                                                    <p className="font-semibold text-gray-900">Mailing Address</p>
                                                    <p className="max-sm:text-sm text-gray-700">
                                                        Argon Medical Systems LLP<br />
                                                        412-413, Radhika Optima Near Yamuna chowk, Mota Varachha Surat, Gujarat 394101
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-6 pt-6 border-t border-blue-200">
                                        <p className="text-sm text-gray-600">
                                            We will respond to your inquiry within 30 days of receipt. For urgent privacy concerns,
                                            please mark your communication as "Urgent - Privacy Matter."
                                        </p>
                                    </div>
                                </div>
                            </section>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;